import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Alert, Card, CardContent, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Error as ErrorIcon, Warning as WarningIcon } from '@mui/icons-material';
import ResetPasswordForm from '../features/Auth/ResetPasswordForm';
import { themeUtils } from '../../theme';
import { EasyCheckButton } from '../common/Controls';

const ResetPasswordContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: themeUtils.gradients.backgroundLight,
  position: 'relative',
  padding: theme.spacing(2),
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: themeUtils.gradients.primary,
    opacity: 0.05,
    zIndex: 0,
  }
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  width: '100%',
  maxWidth: '500px',
}));

const LogoSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(4),
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  textDecoration: 'none',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
  },
}));

const LogoEasy = styled('span')(({ theme }) => ({
  color: '#0f172a', // Dark gray/black
}));

const LogoCheck = styled('span')(({ theme }) => ({
  color: '#2563eb', // Blue
}));

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tokenData, setTokenData] = useState(null);

  useEffect(() => {
    const token = searchParams.get('token');
    const email = searchParams.get('email');

    if (!token || !email) {
      setError('Invalid or missing reset parameters. Please check your email for the correct link.');
      setLoading(false);
      return;
    }

    // Validate token format (basic validation)
    if (token.length < 10) {
      setError('Invalid reset token format. Please request a new password reset.');
      setLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailRegex.test(email)) {
      setError('Invalid email format. Please check your email for the correct link.');
      setLoading(false);
      return;
    }

    setTokenData({ token, email });
    setLoading(false);
  }, [searchParams]);

  const handleSuccess = () => {
    // Navigate to login page after successful password reset
    setTimeout(() => {
      navigate('/login?message=password_reset_success');
    }, 2000);
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (loading) {
    return (
      <ResetPasswordContainer>
        <ContentWrapper>
          <LogoSection>
            <LogoText>
              <LogoEasy>Easy</LogoEasy>
              <LogoCheck>Check</LogoCheck>
            </LogoText>
          </LogoSection>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body1">
                Validating reset link...
              </Typography>
            </CardContent>
          </Card>
        </ContentWrapper>
      </ResetPasswordContainer>
    );
  }

  if (error) {
    return (
      <ResetPasswordContainer>
        <ContentWrapper>
          <LogoSection>
            <LogoText>
              <LogoEasy>Easy</LogoEasy>
              <LogoCheck>Check</LogoCheck>
            </LogoText>
          </LogoSection>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Invalid Reset Link
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {error}
              </Typography>
              <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                <Typography variant="body2">
                  Reset links expire after 1 hour. If your link has expired, please request a new password reset.
                </Typography>
              </Alert>
              <EasyCheckButton
                buttonType="auth"
                variant="contained"
                fullWidth
                onClick={handleBackToLogin}
                sx={{
                  background: themeUtils.gradients.primary,
                  '&:hover': {
                    background: themeUtils.gradients.primaryDark,
                  },
                }}
              >
                Back to Login
              </EasyCheckButton>
            </CardContent>
          </Card>
        </ContentWrapper>
      </ResetPasswordContainer>
    );
  }

  return (
    <ResetPasswordContainer>
      <ContentWrapper>
        <LogoSection>
          <LogoText>
            <LogoEasy>Easy</LogoEasy>
            <LogoCheck>Check</LogoCheck>
          </LogoText>
        </LogoSection>
        <ResetPasswordForm
          username={tokenData.email}
          resetToken={tokenData.token}
          onSuccess={handleSuccess}
        />
      </ContentWrapper>
    </ResetPasswordContainer>
  );
};

export default ResetPasswordPage; 